import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Paper } from "@mui/material";
import { DoubleArrow } from "@mui/icons-material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { useFetch } from "services/hooks/useFetch";
import {
  CustomButtonWithBorder,
  CustomButton,
} from "components/CustomComponents/CustomButton";
import { customAPI } from "services/utils/CustomAPI";
import {
  doubleArrowStyle,
  themes,
  parentBoxStyle,
  childBoxStyleWithColor,
  infoStyle,
  toAlignEnd,
  childBoxStyle,
  nameLength,
} from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { PaymentMode } from "pages/PayRoll/PayRollHeaders";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";

export const EditPayrollGroup = ({
  onClose,
  individualPayrollGrpData,
  reCallApi,
  setGetId,
  setIsEditPanelOpen,
  setHanldeLoader,
  setHasUnsavedChanges,
}) => {
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      is_auto_name: false,
      payrun_cycle: "",
      payment_bank: "",
      mode_of_payment: "",
      name: "",
    },
  });
  const { domain } = useSelector((state) => state.tokenReducer);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const { result: payrunCycleData } = useFetch(
    `${domain}payroll/setup/payruncycle/pay-run-cycles/?company_ids=[${individualPayrollGrpData?.company}]`
  );
  const { result: bankDetailsData } = useFetch(
    `${domain}payroll/setup/paymentbank/payment-bank/?company_ids=[${individualPayrollGrpData?.company}]`
  );

  const formValues = watch();
  const deepEqual = (obj1, obj2) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  };

  useEffect(() => {
    if (individualPayrollGrpData && !initialFormValues) {
      const defaultValues = {
        payrun_cycle: individualPayrollGrpData?.pay_run_cycle_name,
        payment_bank: individualPayrollGrpData?.source_bank_detail_name,
        mode_of_payment: individualPayrollGrpData?.mode_of_payment,
        name: individualPayrollGrpData?.name,
        source_bank_detail: individualPayrollGrpData?.source_bank_detail,
        company: individualPayrollGrpData?.company,
      };
      setInitialFormValues(defaultValues);
      reset(defaultValues);
    }
  }, [individualPayrollGrpData, initialFormValues, reset]);

  useEffect(() => {
    if (initialFormValues && formValues) {
      const hasChanges = !deepEqual(formValues, initialFormValues);
      setHasUnsavedChanges(hasChanges);
    }
  }, [formValues, initialFormValues, setHasUnsavedChanges]);

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    let payload = { ...data };
    const response = await customAPI(
      `${domain}payroll/setup/payrollgroup/payroll-group/${individualPayrollGrpData?.id}/`,
      "PUT",
      payload
    );
    if (response.ok) {
      showToast("success", "Payroll group updated successfully");
      setIsEditPanelOpen(false);
      setValue("button_loader", false);
      setGetId(individualPayrollGrpData?.id);
      setHanldeLoader(true);
      reCallApi(true);
    } else {
      setValue("button_loader", false);
      showToast("error", response?.message);
    }
  };

  const handleCycleName = (event, data) => {
    if (data) {
      setValue("payrun_cycle", data?.name);
      setValue("pay_run_cycle", data.id);
      setValue("is_auto_name", true);
    } else {
      setValue("payrun_cycle", "");
      setValue("pay_run_cycle", "");
    }
  };

  const handlePaymentBank = (event, data) => {
    if (data) {
      setValue("payment_bank", data?.bank_name);
      setValue("source_bank_detail", data?.id);
      setValue("is_auto_name", true);
    } else {
      setValue("payment_bank", "");
      setValue("source_bank_detail", "");
    }
  };

  const handlePaymentMode = (event, data) => {
    if (data) {
      setValue("mode_of_payment", data.label);
      setValue("is_auto_name", true);
    } else {
      setValue("mode_of_payment", "");
    }
  };

  const handlePayrollGroupName = (event) => {
    if (event.target.value.length <= nameLength) {
      setValue("name", event.target.value);
      setValue("is_auto_name", false);
    }
  };

  const isAutoName = watch("is_auto_name");
  const payrunCycle = watch("payrun_cycle");
  const paymentBank = watch("payment_bank");
  const paymentMode = watch("mode_of_payment");

  useEffect(() => {
    if (isAutoName) {
      const autoName = `${payrunCycle || ""}-${paymentBank || ""}-${
        paymentMode || ""
      }`;
      setValue("name", autoName);
    }
  }, [isAutoName, payrunCycle, paymentBank, paymentMode, setValue]);

  return (
    <Box sx={parentBoxStyle}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
        }}
      >
        <Box sx={childBoxStyleWithColor}>
          <Box sx={{ ml: 1 }}>
            <IconButton onClick={onClose} sx={doubleArrowStyle}>
              <DoubleArrow />
            </IconButton>
          </Box>

          <Box sx={{ ml: 2.2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Edit Payroll Group
            </Typography>
            <Typography {...infoStyle}>
              Update pay schedule, payment method, or bank details for an
              existing payroll group.
            </Typography>
          </Box>
        </Box>
      </Paper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box sx={childBoxStyle}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Company Name</Typography>
              <Typography {...infoStyle}>
                Select the company for which this payrun cycle applies
              </Typography>
              <CustomTextBox
                selectedValue={individualPayrollGrpData?.company_name}
                disabledValue={true}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Payrun Cycle</Typography>
              <Typography {...infoStyle}>
                Choose an existing pay schedule to define when salaries are
                processed.
              </Typography>
              <CustomAutoComplete
                dataList={payrunCycleData?.map((obj) => ({
                  ...obj,
                  label: obj?.name,
                }))}
                rules={{
                  ...register("payrun_cycle", {
                    required: "Payrun cycle is required",
                  }),
                }}
                selectedValue={watch("payrun_cycle")}
                updateDataFunction={handleCycleName}
                placeHolder="Select a pay schedule (e.g., Weekly – Monday, Monthly – 10th Day)"
                errorMessage={
                  errors?.payrun_cycle?.message
                    ? errors?.payrun_cycle?.message
                    : ""
                }
                textBoxError={errors?.payrun_cycle ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Payment Bank</Typography>
              <Typography {...infoStyle}>
                Select the bank account through which salaries for this group
                will be disbursed.
              </Typography>
              <CustomAutoComplete
                dataList={bankDetailsData?.map((obj) => ({
                  ...obj,
                  label: obj?.bank_name,
                }))}
                rules={{
                  ...register("payment_bank", {
                    required: "Payment bank is required",
                  }),
                }}
                selectedValue={watch("payment_bank")}
                updateDataFunction={handlePaymentBank}
                placeHolder="Select a bank (e.g., SBI, HDFC, ICICI)"
                errorMessage={
                  errors?.payment_bank?.message
                    ? errors?.payment_bank?.message
                    : ""
                }
                textBoxError={errors?.payment_bank ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Payment Mode</Typography>
              <Typography {...infoStyle}>
                Choose how employees in this group will receive their salary
                payments.
              </Typography>
              <CustomAutoComplete
                dataList={PaymentMode}
                rules={{
                  ...register("mode_of_payment", {
                    required: "Payment mode is required",
                  }),
                }}
                selectedValue={watch("mode_of_payment")}
                updateDataFunction={handlePaymentMode}
                placeHolder="Select a payment method (e.g., Cash, Bank Transfer, Cheque)"
                errorMessage={
                  errors?.mode_of_payment?.message
                    ? errors?.mode_of_payment?.message
                    : ""
                }
                textBoxError={errors?.mode_of_payment ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Payroll Group Name 
              </Typography>
              <Typography {...infoStyle}>
                Enter a name to identify this payroll group easily during
                processing.
              </Typography>
              <CustomTextBox
                selectedValue={watch("name")}
                rules={{
                  ...register("name", {
                    required: "Payroll group name is required",
                    minLength: {
                      value: 3,
                      message: "Minium length is 3 characters",
                    },
                    validate: {
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={handlePayrollGroupName}
                placeHolder="Enter group name (e.g., Weekly Staff, Monthly Contract Team)"
                errorMessage={
                  errors?.name?.message ? errors?.name?.message : ""
                }
                textBoxError={errors.name ? true : false}
              />
            </Box>
            <Box sx={toAlignEnd}>
              <CustomButtonWithBorder
                actionName="Cancel"
                typeName="button"
                actionFuntion={() => onClose()}
              />
              <CustomButton
                actionFuntion={handleSubmit(onSubmit)}
                actionName="Update"
                typeName="submit"
                disableAction={watch("button_loader")}
              />
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
